import {AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {AppSettings} from 'app/app.settings';
import {SupplierState} from 'app/profile/store/state/supplierstate.state';
import {Subscription} from 'rxjs/Subscription'
import {SelectCurrentUser, SelectPreventativeInProgress} from 'app/profile/store/app.selector';
import {SupplierContactMap, UserDetails} from 'app/profile/models/user.model';
import {GetPreventativeInProgress, GetSupplierChannelListByCode} from 'app/profile/store/app.actions';
import {SidePanelDialogComponent} from 'app/shared/components/side-panel-dialog/side-panel-dialog.component';
import {SupplierContactPropertyValue} from 'app/profile/models/supplier.model';
import {EinvoicingChannelsGridComponent} from './einvoicing-channels-grid/einvoicing-channels-grid.component';
import {EInvoicingService} from 'app/profile/services/einvoicing-channels.service';
import {TranslocoService} from "@ngneat/transloco";
import {LanguageService} from "../../../core/services/language.service";

@Component({
  selector: 'app-einvoicing-channels',
  templateUrl: './einvoicing-channels.component.html',
  styleUrls: ['./einvoicing-channels.component.scss']
})
export class EinvoicingChannelsComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('createChannelRequestSideBar', {static: false}) sidePanel: SidePanelDialogComponent;
  @ViewChild('gridView', {static: true}) gridView: EinvoicingChannelsGridComponent;
  @ViewChild('banner1') banner1: ElementRef;
  @ViewChild('banner2') banner2: ElementRef;

  code = '';
  subscription: Subscription = new Subscription();
  userDetails: UserDetails;
  isPrimary = false;
  isProfileAdmin = false;
  isEDI = false;
  isFinance = false;
  isFinanceLead = false;
  isSupplierCanEdit = false;
  channelRequestEnabled = false;
  confirmMessage = 'There are unsaved changes. Are you sure you want to proceed?';
  confirmTitle = 'Unsaved Information';
  preventativeHold = false;
  wsmUrl: string;
  guidelineUrl = '';

  // html tags that need to interpolated during translation
  banner1HtmlTags = [];
  banner2HtmlTags = [
    `<strong>`,
    `<u>`, `</u>`,
    `</strong>`,
    `<span style="color: #c00;">`, // using class not work
    `<strong>`, `</strong>`,
    `</span>`,
    `<span style="color: #c00;">`,
    `<strong>`, `</strong>`,
    `</span>`,
    `<strong>`, `</strong>`,
    `<strong>`, `</strong>`,
    `<strong>`, `</strong>`,
    `<strong>`, `</strong>`,
    `<u>`, `</u>`,
    `<strong>`, `</strong>`,
    `<strong>`, `</strong>`,
    `<span style="color: #c00;">`,
    `<strong>`, `</strong>`,
    `</span>`,
  ];

  contacts: SupplierContactPropertyValue[];

  constructor(private store: Store<SupplierState>,
              private activatedRoute: ActivatedRoute,
              private config: AppSettings,
              private eInvoicingService: EInvoicingService,
              private languageService: LanguageService,
              private translocoService: TranslocoService,
  ) {
    this.guidelineUrl = `${this.config.PUBLIC_URL}/documents`;
    this.banner1HtmlTags = [
      `<span style="color: #c00;">`, // using class not work
      `<strong>`,
      `</strong>`,
      `</span>`,
      `<a target="_blank" href="${this.guidelineUrl}">`,
      `</a>`,
    ];

    // subscribe language change so that we can intercept the translation process
    this.languageService.getSelectedLanguageAsync()
      .subscribe(langID => this.renderInnerHtmlTranslation(langID));
  }

  ngOnInit(): void {
    this.activatedRoute.parent.params.subscribe((params) => {
      if (!params['code']) {
        return;
      }
      this.code = params['code'];
    })

    const user = this.store.pipe(select(SelectCurrentUser)).subscribe((result) => {
      if (!result) {
        return;
      }

      this.userDetails = result;
      if (this.userDetails.supplierContact && this.userDetails.supplierContact.supplierMaps) {
        const supplierMap: SupplierContactMap = this.userDetails.supplierContact.supplierMaps
          .find(x => x.supplierCode === this.code) as SupplierContactMap;
        this.isPrimary = supplierMap.userRoles.some(x => x.role.toLocaleLowerCase() === "primary");
        this.isProfileAdmin = supplierMap.userRoles.some(x => x.role.toLocaleLowerCase() === "profile admin");
        this.isEDI = supplierMap.userRoles.some(x => x.role.toLocaleLowerCase() === 'edi');
        this.isFinance = supplierMap.userRoles.some(x => x.role.toLocaleLowerCase() === 'finance' || x.role.toLocaleLowerCase() === "finance lead");
        this.isFinanceLead = supplierMap.userRoles.some(x => x.role.toLocaleLowerCase() === "finance lead");
        this.isSupplierCanEdit = this.isEDI || this.isFinance || this.isPrimary;
      }
    });

    this.store.dispatch(new GetSupplierChannelListByCode(this.code));
    this.subscription.add(user);

    this.subscription.add(this.eInvoicingService.getChannelRequestFeatureFlag().subscribe((channelRequestEnabled: boolean) => {
      if (!channelRequestEnabled) {
        return;
      }
      this.channelRequestEnabled = channelRequestEnabled;
    }));

    this.wsmUrl = `${this.config.WSM_URL}edit/${this.code}/channel`; // Link to supplier master for Tesla users
    if (this.code) {
      this.store.dispatch(new GetPreventativeInProgress(this.code));
      this.store.pipe(select(SelectPreventativeInProgress)).subscribe(x => {
        if (x) {
          this.preventativeHold = x;
        }
      });
    }
  }

  ngAfterViewInit() {
    this.renderInnerHtmlTranslation(this.languageService.getSelectedLanguage());
  }

  onButtonClick() {
    this.sidePanel.open();
  }

  onCreateRequestComplete() {
    this.sidePanel.close(true);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Renders the translation in the innerHtml to support rich text.
   * @param langID language id
   */
  renderInnerHtmlTranslation(langID: string) {
    if (this.banner1 || this.banner2) {
      // subscribe this to make sure the translation is already available
      this.translocoService.selectTranslation(langID)
        .subscribe(_ => {
          if (this.banner1) {
            const translatedText = this.translocoService
              .translateObject("EinvoicingChannelRequestBanner1", this.getParamMap(this.banner1HtmlTags), langID);
            if (translatedText && translatedText !== '') {
              this.banner1.nativeElement.innerHTML = translatedText;
            }
          }
          if (this.banner2) {
            const translatedText = this.translocoService
              .translateObject("EinvoicingChannelRequestBanner2", this.getParamMap(this.banner2HtmlTags), langID);
            if (translatedText && translatedText !== '') {
              this.banner2.nativeElement.innerHTML = translatedText;
            }
          }
        })
    }
  }

  getParamMap(arr: string[]) {
    const params = {}

    for (let i = 0; i < arr.length; i++) {
      params[`param${i + 1}`] = arr[i];
    }

    return params;
  }
}
